/**
 * Game page
 */
import React, {useState, useEffect} from "react";
import {useParams, useNavigate} from "react-router-dom";
import Container from "./../components/core/Container";
import GameBanner from "./../components/game/GameBanner";
import GameRecap from "../components/game/GameRecap.js";
import GameSidebar from "../components/game/GameSidebar.js";
import {useQuery} from "@tanstack/react-query";
import GameStats from "../components/game/GameStats.js";
import GamePreview from "../components/game/GamePreview.js";
import {Helmet} from "react-helmet-async";
import PageSidebarContainer from "../components/core/PageSidebarContainer";
import ContentContainer from "../components/core/ContentContainer";
import Loading from "../components/core/Loading";

const processGameData = (jsonData) => {
    return jsonData;
};

const getGameStatus = (played) => {
    switch (played) {
        case -1:
            return "Provisional";
        case 0:
            return "Preview";
        case 1:
            return "Played";
        case 2:
            return "Pending";
        case 3:
            return "Pending";
        // 4 = Fixture Issue Raised
        case 4:
            return "Preview"
        case 5:
            return "Postponed";
        case 6:
            return "Cancelled";
        case 7:
            return "Abandoned";

        default:
            return "Unknown";
    }
};

const Game = () => {
    const {gameId, tab} = useParams(); // Get gameId and tab from URL
    const navigate = useNavigate(); // Initialize navigate for updating URL
    const [showSideBar, setShowSideBar] = useState(true);
    const [activeTab, setActiveTab] = useState(tab || "preview"); // Default
    const [tabContents, setTabContents] = useState({}); // Initialize tabContents as empty object

    const {isLoading, error, data, refetch} = useQuery({
        staleTime: 3600000,
        queryKey: ["game", gameId],
        queryFn: () =>
            fetch(`${process.env.REACT_APP_BUIHA_API_URL}/game/${gameId}/`)
                .then((res) => res.json())
                .then((json) => {
                    const game_data = processGameData(json);
                    game_data.played_status = getGameStatus(game_data.played);

                    const url = `https://gamesheet.buiha.org.uk/g/${gameId}/`;
                    const contents = {
                        stats: <GameStats data={game_data}/>,
                        recap: <GameRecap data={game_data}/>,
                        preview: <GamePreview data={game_data}/>,
                        gamesheet: (
                            <iframe
                                title="Game Sheet"
                                className="embed-responsive-item mt-4"
                                src={url}
                                frameBorder="0"
                                allowFullScreen
                                style={{width: "100%", height: "850px"}}
                            ></iframe>
                        ),
                    };

                    setTabContents(contents); // Set tabContents after fetching data

                    return game_data;
                }),
    });

// Sync the active tab with the URL param when the URL changes (e.g., via back/forward navigation)
    useEffect(() => {
        if (!isLoading && data) {
            if (tab) {
                setActiveTab(tab); // If there's a tab parameter, set it
            } else {
                if (data.status === "END") {
                    setActiveTab("stats"); // Set to "stats" tab if the game has ended
                } else {
                    setActiveTab("preview"); // Set to "preview" tab if the game is ongoing
                }
            }
        }
    }, [tab, isLoading, data]); // Dependencies include isLoading and data

    // Handle tab click and update the URL accordingly
    const handleTabClick = (tab) => {
        setActiveTab(tab);
        setShowSideBar(tab !== "preview");
        navigate(`/game/${gameId}/${tab}`); // Update the URL with the new tab
    };

    // Re-fetch data if gameId changes
    useEffect(() => {
        refetch();
    }, [gameId, refetch]);

    // Handle loading and error states
    if (isLoading)
        return (
            <>
                <GameBanner
                    activeTab={activeTab}
                    handleTabClick={handleTabClick}
                    data={{}}
                />
                <Container>
                  <Loading/>
                </Container>
            </>
        );

    if (error) return "An error has occurred: " + error.message;

    // Ensure tabContents has a default value for the current active tab
    const contentToRender = tabContents[activeTab] || "Loading content...";

    // Render the active tab content only if data is available
    return (
        <>
            {data && (
                <>
                    <Helmet>
                        <title>
                            {data.home_team.name} vs {data.away_team.name} {data.full_date} |
                            BUIHA Game
                        </title>
                        <meta
                            name="description"
                            content={`Game page for the ${data.home_team.name} vs ${data.away_team.name} game, ${data.full_date} at ${data.location} in the ${data.division.name} division of the BUIHA`}
                        />
                        <link rel="canonical" href={`https://buiha.org/game/${gameId}`}/>
                        <link rel="icon" type="image/png" href="/favicon.png"/>
                    </Helmet>

                    <GameBanner
                        activeTab={activeTab}
                        handleTabClick={handleTabClick}
                        data={data}
                    />

                    <Container>
                        <PageSidebarContainer
                            children={
                                <>
                                    <ContentContainer
                                        children={<>{contentToRender}</>}
                                        extraClasses={activeTab === "recap" ? "xs:px-8 sm:max-xl:px-16 xs:mt-1" : "xs:mt-1"}
                                        mode={activeTab === "stats" ? "gamestats" : ""}
                                    />
                                    {data.played === 1 ? <GameSidebar game_data={data} headless={false}/> : null}
                                </>
                            }
                        />
                    </Container>
                </>
            )}
        </>
    );
};

export default Game;
